import { CellStatus, DataTable, Status } from '@fluentsms/agentnet-web-components';
import { Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  CellClickedEvent,
  ColDef,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  ITooltipParams,
  RowNode,
  SelectionChangedEvent,
  SetFilterValuesFuncParams,
} from 'ag-grid-enterprise';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { useCallback, useRef, useState } from 'react';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import NoResults from 'ui-kit/components/noResults/NoResults';
import ManageSearchIcon from 'ui-kit/icons/ManageSearchIcon';
import { InvoiceSearchResult } from './types';

const toTitleCase = (str: string | undefined): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

interface InvoicesGridProps {
  invoices: InvoiceSearchResult[];
  loading: boolean;
  selectedInvoices?: InvoiceSearchResult[];
  onSelectionChanged?: (selectedInvoices: InvoiceSearchResult[]) => void;
  onInvoiceView?: (invoice: InvoiceSearchResult) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableDefault: {
      height: 'calc(100vh - 575px)',
      fontFamily: theme.typography.fontFamily,
      minHeight: '400px',
    },
    paddingX: {
      paddingLeft: theme.spacing(0),
      paddingRight: theme.spacing(0),
      width: 'auto',
    },
    noRows: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 220,
      justifyContent: 'center',
    },
    rowCountText: {
      padding: theme.spacing(2, 0),
      fontSize: '0.875rem',
    },
    rowCountContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(2, 0),
    },
  }),
);

// Status colors matching standard AgentNet icon set
const statusColors: { [status: string]: string } = {
  // NEED COLORS
  Closed: '#5E35B1', // Purple
  Completed: '#4CAF50', // Green
  Submitted: '#1976D2', // Blue
  'Posted To Oracle': '#1976D2', // Blue
  Pending: '#F57C00', // Orange
  Error: '#D32F2F', // Red
  'Data Error': '#D32F2F', // Red
  Paid: '#388E3C', // Green
  Processing: '#F57C00', // Orange
  Overdue: '#D32F2F', // Red
  Cancelled: '#757575', // Gray
};

const formatCurrency = (value: number): string => {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const dateComparator = (filterLocalDateAtMidnight: Date, cellValue: string) => {
  const cellDate = new Date(cellValue);
  cellDate.setHours(0, 0, 0, 0);
  if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) return 0;
  if (cellDate.getTime() < filterLocalDateAtMidnight.getTime()) return -1;
  if (cellDate.getTime() > filterLocalDateAtMidnight.getTime()) return 1;
  return 0;
};

const InvoicesGrid = ({
  invoices,
  loading,
  selectedInvoices = [],
  onSelectionChanged,
  onInvoiceView,
}: InvoicesGridProps) => {
  const classes = useStyles();
  const gridApiRef = useRef<GridApi<InvoiceSearchResult> | null>(null);
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);

  const totalSelectedAmount = selectedInvoices.reduce((sum, invoice) => sum + (invoice.netAmount || 0), 0);

  const headerCheckboxRenderer = () => {
    return <input type="checkbox" data-testid="OrderManagement-Invoices-AllCheckbox" />;
  };

  const rowCheckboxRenderer = (params: { node: RowNode }) => {
    return <input type="checkbox" data-testid={`OrderManagement-Invoices-Checkbox-${params.node.id}`} />;
  };

  const onGridReady = (params: GridReadyEvent<InvoiceSearchResult>) => {
    gridApiRef.current = params.api;
    setDisplayedRowCount(params.api.getDisplayedRowCount());
  };

  const onFilterChanged = (e: FilterChangedEvent): void => {
    setDisplayedRowCount(e.api.getDisplayedRowCount());
  };

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<InvoiceSearchResult>) => {
      const selectedRows = event.api.getSelectedRows();
      onSelectionChanged?.(selectedRows);
    },
    [onSelectionChanged],
  );

  const dateRenderer = useCallback((params: ICellRendererParams<InvoiceSearchResult>) => {
    if (!params.value) return <></>;
    // Parse ISO string and format as MM/dd/yyyy without timezone conversion
    const date = parseISO(params.value.toString().split('T')[0]);
    return <>{format(date, 'MM/dd/yyyy')}</>;
  }, []);

  const currencyRenderer = useCallback((params: ICellRendererParams<InvoiceSearchResult>) => {
    if (params.value === undefined || params.value === null) return <></>;
    return <div style={{ textAlign: 'right', paddingRight: '16px' }}>{formatCurrency(params.value)}</div>;
  }, []);

  const getStatusTooltip = (status: string): string => {
    const tooltips: { [key: string]: string } = {
      Closed: 'Invoice has been closed and payment completed',
      Submitted: 'Invoice has been submitted for processing',
      Pending: 'Invoice is awaiting action or approval',
      Error: 'Invoice has encountered an error and requires attention',
      Processing: 'Invoice is currently being processed',
    };
    return tooltips[status] || status;
  };

  const columnDefs: ColDef<InvoiceSearchResult>[] = [
    {
      colId: 'checkboxColumn',
      maxWidth: 40,
      filter: false,
      floatingFilter: false,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
      showDisabledCheckboxes: false,
      pinned: 'left',
      headerComponent: headerCheckboxRenderer,
      cellRenderer: rowCheckboxRenderer,
      resizable: false,
      suppressMovable: true,
      suppressColumnsToolPanel: true,
    },
    {
      colId: 'statusColumn',
      field: 'status',
      headerName: 'Invoice Status',
      cellRenderer: (params: ICellRendererParams<InvoiceSearchResult>) => {
        const status = toTitleCase(params.value) as Status;
        return <CellStatus status={status} colorsByStatus={statusColors} />;
      },
      filter: 'agSetColumnFilter',
      filterParams: {
        values: (params: SetFilterValuesFuncParams) => {
          const defaultValues = ['Closed', 'Submitted', 'Pending', 'Error', 'Processing'];
          const dataValues = new Set(defaultValues);
          params.api.forEachNode((node) => {
            if (node.data?.status) {
              dataValues.add(toTitleCase(node.data.status));
            }
          });
          params.success(Array.from(dataValues));
        },
      },
      minWidth: 160,
      maxWidth: 200,
      tooltipValueGetter: (params: ITooltipParams<InvoiceSearchResult>) => {
        return params.value ? getStatusTooltip(toTitleCase(params.value)) : '';
      },
    },
    {
      colId: 'invoiceNumberColumn',
      field: 'invoiceNo',
      headerName: 'Invoice Number',
      filter: 'agTextColumnFilter',
      minWidth: 250,
      maxWidth: 280,
      tooltipField: 'invoiceNo',
    },
    {
      colId: 'orderNumberColumn',
      field: 'orderNo',
      headerName: 'Order Number',
      filter: 'agNumberColumnFilter',
      minWidth: 240,
      maxWidth: 270,
      tooltipField: 'orderNo',
    },
    {
      colId: 'fileNumberColumn',
      field: 'fileNumber',
      headerName: 'File Number',
      filter: 'agTextColumnFilter',
      minWidth: 240,
      maxWidth: 280,
      tooltipField: 'fileNumber',
    },
    {
      colId: 'accountNumberColumn',
      field: 'accountNo',
      headerName: 'Account Number',
      filter: 'agTextColumnFilter',
      minWidth: 180,
      maxWidth: 220,
      tooltipField: 'accountNo',
    },
    {
      colId: 'accountNameColumn',
      field: 'accountName',
      headerName: 'Account Name',
      filter: 'agTextColumnFilter',
      minWidth: 200,
      maxWidth: 300,
      tooltipField: 'accountName',
    },
    {
      colId: 'invoiceDateColumn',
      field: 'invoiceDate',
      headerName: 'Invoice Date',
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: dateComparator,
        browserDatePicker: true,
        dateFormat: 'yyyy-mm-dd',
        inRangeInclusive: true,
      },
      cellRenderer: dateRenderer,
      minWidth: 220,
      maxWidth: 250,
      tooltipValueGetter: (params: ITooltipParams<InvoiceSearchResult>) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString('en-US');
      },
    },
    {
      colId: 'grossColumn',
      field: 'grossAmount',
      headerName: 'Gross',
      filter: 'agNumberColumnFilter',
      cellRenderer: currencyRenderer,
      minWidth: 200,
      maxWidth: 240,
      tooltipValueGetter: (params: ITooltipParams<InvoiceSearchResult>) => {
        if (params.value === undefined || params.value === null) return '';
        return formatCurrency(params.value);
      },
    },
    {
      colId: 'netColumn',
      field: 'netAmount',
      headerName: 'Net',
      filter: 'agNumberColumnFilter',
      cellRenderer: currencyRenderer,
      minWidth: 200,
      maxWidth: 240,
      tooltipValueGetter: (params: ITooltipParams<InvoiceSearchResult>) => {
        if (params.value === undefined || params.value === null) return '';
        return formatCurrency(params.value);
      },
    },
    {
      colId: 'viewDetailsColumn',
      headerName: '',
      filter: false,
      floatingFilter: false,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<InvoiceSearchResult>) => {
        return (
          <span
            style={{ position: 'relative', top: '10px' }}
            data-testid={`OrderManagement-Invoices-ViewDetails-${params.data?.invoiceNo}`}
          >
            {ManageSearchIcon({ fontSize: 'small' })}
          </span>
        );
      },
      onCellClicked: (event: CellClickedEvent<InvoiceSearchResult>) => {
        if (event.data && onInvoiceView) {
          onInvoiceView(event.data);
        }
      },
      minWidth: 40,
      maxWidth: 40,
      cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
      tooltipValueGetter: () => 'View invoice details',
      pinned: 'right',
    },
  ];

  const defaultColDef: ColDef = {
    editable: false,
    filter: true,
    flex: 1,
    floatingFilter: true,
    resizable: true,
    sortable: true,
    suppressHeaderFilterButton: true,
    suppressHeaderMenuButton: false,
    minWidth: 100,
  };

  const CustomOverlayLoading = () => {
    return (
      <div className={classes.noRows}>
        <LoadingSpinner status="pending" />
      </div>
    );
  };

  const CustomOverlayNoRows = () => {
    return (
      <div className={classes.noRows}>
        <NoResults
          showButton={false}
          type="results"
          titleText="No invoices match your search criteria."
          pText="Try adjusting your search filters to find what you're looking for."
        />
      </div>
    );
  };

  if (loading) {
    return <CustomOverlayLoading />;
  }

  return (
    <>
      <div className={clsx('ag-theme-alpine', classes.tableDefault, classes.paddingX)}>
        <DataTable
          onGridReady={onGridReady}
          rowData={invoices}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onFilterChanged={onFilterChanged}
          onSelectionChanged={handleSelectionChanged}
          animateRows={true}
          noRowsOverlayComponent={'customOverlayNoRows'}
          components={{
            customOverlayNoRows: CustomOverlayNoRows,
          }}
          gridOptions={{
            suppressRowClickSelection: true,
            rowSelection: 'multiple',
          }}
        />
      </div>
      <div className={classes.rowCountContainer}>
        <Typography variant="body2">
          Showing {displayedRowCount} of {invoices.length} invoices
        </Typography>
        {selectedInvoices.length > 0 && (
          <Typography variant="body2">
            Selecting {selectedInvoices.length} invoice(s) for {formatCurrency(totalSelectedAmount)}
          </Typography>
        )}
      </div>
    </>
  );
};

export default InvoicesGrid;
